import Phaser from 'phaser';
import {
  COLOR_CSS,
  COLORS,
  DURATIONS,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  SAFE_AREA,
} from '../config';
import { Button } from '../ui/Button';
import { createCommandDeckBackground, createSceneHeader, drawChamferedPanel } from '../ui/SceneChrome';
import { saveSystem } from '../systems/SaveSystem';
import { logSceneEvent } from '../utils/automation';

type LevelState = 'locked' | 'unlocked' | 'completed' | 'current';

interface LevelButton {
  container: Phaser.GameObjects.Container;
  background: Phaser.GameObjects.Graphics;
  levelText: Phaser.GameObjects.Text;
  icon: Phaser.GameObjects.Image | null;
  state: LevelState;
  level: number;
}

export class LevelSelectScene extends Phaser.Scene {
  private levelButtons: LevelButton[] = [];
  private currentLevelPulseTween: Phaser.Tweens.Tween | null = null;

  constructor() {
    super({ key: 'LevelSelectScene' });
  }

  create(): void {
    logSceneEvent('LevelSelectScene');
    saveSystem.load();
    this.cameras.main.fadeIn(DURATIONS.screenTransition, 0, 0, 0);

    const currentTier = saveSystem.getCurrentTier();
    const highestLevel = saveSystem.getHighestLevel();

    createCommandDeckBackground(this);
    createSceneHeader(this, {
      title: 'Mission Grid',
      kicker: `Breach campaign // tier ${String(currentTier).padStart(2, '0')}`,
      y: SAFE_AREA.top + 45,
    });
    this.createCampaignMeter(highestLevel);
    this.createLevelGrid(highestLevel, currentTier);
    this.createFooterActions();
    this.bindKeyboardShortcuts();
  }

  private createCampaignMeter(highestLevel: number): void {
    const y = SAFE_AREA.top + 94;
    const width = GAME_WIDTH - 64;
    const track = this.add.graphics();
    track.fillStyle(0x02070D, 0.78);
    track.fillRoundedRect(32, y, width, 5, 2);
    track.fillStyle(COLORS.player, 0.82);
    track.fillRoundedRect(32, y, width * Phaser.Math.Clamp((highestLevel - 1) / 9, 0.03, 1), 5, 2);

    this.add.text(32, y + 11, 'SECTOR 01', {
      fontFamily: FONTS.numbers,
      fontSize: '10px',
      color: COLOR_CSS.uiTextSecondary,
      letterSpacing: 1,
    });
    this.add.text(GAME_WIDTH - 32, y + 11, 'CORE 10', {
      fontFamily: FONTS.numbers,
      fontSize: '10px',
      color: COLOR_CSS.uiTextSecondary,
      letterSpacing: 1,
    }).setOrigin(1, 0);
  }

  private createLevelGrid(highestLevel: number, currentTier: number): void {
    const columns = 2;
    const cardWidth = 145;
    const cardHeight = 68;
    const gapX = 18;
    const gapY = 16;
    const gridWidth = columns * cardWidth + gapX;
    const startX = (GAME_WIDTH - gridWidth) / 2 + cardWidth / 2;
    const startY = SAFE_AREA.top + 160;

    for (let i = 0; i < 10; i++) {
      const level = i + 1;
      const col = i % columns;
      const row = Math.floor(i / columns);
      const state = this.getLevelState(level, highestLevel, currentTier);
      const levelButton = this.createLevelButton(
        startX + col * (cardWidth + gapX),
        startY + row * (cardHeight + gapY),
        level,
        state,
        cardWidth,
        cardHeight
      );
      this.levelButtons.push(levelButton);
      if (state === 'current') this.addPulseAnimation(levelButton);
    }
  }

  private getLevelState(level: number, highestLevel: number, currentTier: number): LevelState {
    if (level < highestLevel) {
      return saveSystem.getLevelScore(level, currentTier) > 0 ? 'completed' : 'unlocked';
    }
    if (level === highestLevel) return 'current';
    return 'locked';
  }

  private createLevelButton(
    x: number,
    y: number,
    level: number,
    state: LevelState,
    width: number,
    height: number
  ): LevelButton {
    const container = this.add.container(x, y);
    const background = this.add.graphics();
    const isLocked = state === 'locked';
    const border = state === 'current'
      ? COLORS.player
      : state === 'completed'
        ? 0x00D4FF
        : 0x31566C;

    drawChamferedPanel(
      background,
      -width / 2,
      -height / 2,
      width,
      height,
      isLocked ? 0x0B1823 : 0x102536,
      border,
      isLocked ? 0.5 : 0.96,
      10
    );
    background.fillStyle(border, state === 'current' ? 1 : 0.55);
    background.fillRect(-width / 2 + 10, -height / 2 + 5, 34, 3);
    container.add(background);

    const sector = this.add.text(-width / 2 + 16, -13, `S-${String(level).padStart(2, '0')}`, {
      fontFamily: FONTS.numbers,
      fontSize: '10px',
      color: isLocked ? '#52697A' : '#73F7FF',
      letterSpacing: 1,
    }).setOrigin(0, 0.5);
    container.add(sector);

    const levelText = this.add.text(-width / 2 + 16, 13, String(level).padStart(2, '0'), {
      fontFamily: FONTS.display,
      fontSize: '25px',
      color: state === 'current' ? COLOR_CSS.player : isLocked ? '#52697A' : COLOR_CSS.uiText,
      fontStyle: 'bold',
      letterSpacing: 2,
    }).setOrigin(0, 0.5);
    container.add(levelText);

    let icon: Phaser.GameObjects.Image | null = null;
    if (state === 'locked') {
      icon = this.add.image(width / 2 - 25, 0, 'icon-lock').setScale(0.55).setAlpha(0.5);
      container.add(icon);
    } else {
      const status = state === 'completed' ? 'CLEARED' : state === 'current' ? 'ACTIVE' : 'OPEN';
      const statusColor = state === 'completed' ? '#73F7FF' : state === 'current' ? '#39FF14' : '#8BA9BC';
      container.add(
        this.add.text(width / 2 - 14, 2, status, {
          fontFamily: FONTS.numbers,
          fontSize: '10px',
          color: statusColor,
          letterSpacing: 1,
        }).setOrigin(1, 0.5)
      );
      const dot = this.add.circle(width / 2 - 23, -16, 3, border, 1);
      container.add(dot);
    }

    if (!isLocked) {
      const hitArea = this.add.rectangle(0, 0, width, height, 0x000000, 0).setInteractive({ useHandCursor: true });
      container.add(hitArea);
      hitArea.on('pointerdown', () => container.setScale(0.97));
      hitArea.on('pointerout', () => container.setScale(1));
      hitArea.on('pointerup', () => {
        container.setScale(1);
        this.startLevel(level);
      });
    }

    return { container, background, levelText, icon, state, level };
  }

  private addPulseAnimation(levelButton: LevelButton): void {
    this.currentLevelPulseTween = this.tweens.add({
      targets: levelButton.container,
      scaleX: 1.025,
      scaleY: 1.025,
      duration: 850,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });
  }

  private createFooterActions(): void {
    const y = GAME_HEIGHT - SAFE_AREA.bottom - 40;
    new Button(this, {
      x: GAME_WIDTH / 2 - 82,
      y,
      text: 'Intel',
      variant: 'secondary',
      width: 144,
      height: 46,
      onClick: () => this.goToStats(),
    });
    new Button(this, {
      x: GAME_WIDTH / 2 + 82,
      y,
      text: 'Back',
      variant: 'secondary',
      width: 144,
      height: 46,
      onClick: () => this.goBack(),
    });
  }

  private bindKeyboardShortcuts(): void {
    const keys = ['ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE', 'ZERO'];
    keys.forEach((key, index) => {
      this.input.keyboard?.on(`keydown-${key}`, () => this.startLevel(index + 1));
    });
    this.input.keyboard?.on('keydown-A', () => this.startLevel(1));
    this.input.keyboard?.on('keydown-B', () => this.startLevel(2));
  }

  private startLevel(level: number): void {
    this.currentLevelPulseTween?.stop();
    this.transitionTo('GameScene', { level, tier: saveSystem.getCurrentTier() });
  }

  private goToStats(): void {
    this.transitionTo('StatsScene');
  }

  private goBack(): void {
    this.currentLevelPulseTween?.stop();
    this.transitionTo('TitleScene');
  }

  private transitionTo(sceneKey: string, data?: object): void {
    this.cameras.main.fadeOut(DURATIONS.screenTransition, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => this.scene.start(sceneKey, data));
  }
}
